/**
 * Agent analytics contract: sessions with semantic enrichment, the discovered use-case taxonomy,
 * outcomes, product-manager funnels, conversation paths, pain points, dynamic signal definitions,
 * insights and agent economics (docs 14 and 15).
 *
 * Everything here is derived from one primitive, the enriched session: each session (a conversation
 * or a task run) is enriched once by the analytics model into structured facts, and every view below
 * is an aggregation over those facts.
 */

import type {
  DetectionTier,
  ISODate,
  Money,
  PackId,
  Severity,
  SignalFamily,
  SignalRef,
} from "./types";

// ─── Sessions & enrichment ───────────────────────────────────────────────────

export const SESSION_OUTCOMES = [
  "resolved",
  "converted",
  "partially_resolved",
  "escalated",
  "abandoned",
  "failed",
  "unknown",
] as const;
export type SessionOutcome = (typeof SESSION_OUTCOMES)[number];

/** Outcomes that count as success for success-rate metrics. */
export const SUCCESS_OUTCOMES: readonly SessionOutcome[] = ["resolved", "converted"];

/**
 * Where an outcome came from, in order of trust: an explicit event the customer sent
 * (`track("order_placed")`), a joined business system (ticket reopened, payment settled), or the
 * analytics model's inference from the transcript.
 */
export type OutcomeSource = "event" | "business" | "inferred";

export const FRICTION_KINDS = [
  "rephrase",
  "repeat_question",
  "human_request",
  "negative_sentiment",
  "agent_loop",
  "dead_end",
  "unanswered",
  "tool_failure",
  "slow_response",
  "knowledge_gap",
  "unsupported_request",
  "policy_refusal",
] as const;
export type FrictionKind = (typeof FRICTION_KINDS)[number];

/**
 * How much of a session the analytics model saw. `full` = enriched from (PII-redacted) content;
 * `metadata_only` = privacy mode "features" (no content leaves the customer; structural facts only);
 * `sampled_out` = embeddings and classifiers only, the model pass was skipped by the sampling policy;
 * `pending` = session still open or queued.
 */
export type EnrichmentLevel = "full" | "metadata_only" | "sampled_out" | "pending";

export type Channel = "chat" | "voice" | "email" | "api" | "slack" | "ide" | "batch";

export interface FrictionMarker {
  kind: FrictionKind;
  /** Turn index in the session transcript. */
  turn: number;
  note: string;
}

/**
 * The session map (Use cases → Map): the latest discovery run's clusters of recent sessions, each
 * session at a 2D position where nearby means similar. Null `runAt` before the first run.
 */
export interface SessionMap {
  runAt: ISODate | null;
  clusters: Array<{
    cluster: number;
    /** The use case the cluster became or matched; null when it was too small or generic to name. */
    useCaseId: string | null;
    name: string | null;
    size: number;
    successRate: number;
    outcomes: Partial<Record<SessionOutcome, number>>;
    examples: string[];
  }>;
  points: Array<{ sessionId: string; cluster: number; agentId: string; x: number; y: number; outcome: SessionOutcome; intent: string | null; endedAt: ISODate }>;
  /** True when more sessions were clustered than returned. */
  truncated: boolean;
}

/** Sessions closest to one in meaning (the session index), best first. */
export interface SimilarSessions {
  items: Array<SessionSummary & { similarity: number }>;
  /** False while the session's own vector isn't in the index yet; matches then come from its text. */
  indexed: boolean;
}

export interface SessionSummary {
  id: string;
  agentId: string;
  agentName: string;
  agentVersion: string;
  endUserId: string | null;
  endUser: string | null;
  /** End-user segment from the customer's identify() traits (e.g. "member", "guest", "enterprise"). */
  segment: string | null;
  channel: Channel;
  startedAt: ISODate;
  endedAt: ISODate;
  durationMs: number;
  turns: number;
  traceIds: string[];
  costUsd: number;
  tokens: number;
  /** Leaf use case from the discovered taxonomy, and its path (e.g. ["Returns", "Damaged item"]). */
  useCaseId: string | null;
  useCasePath: string[];
  /** One-line intent in the user's words, normalized. */
  intent: string;
  outcome: SessionOutcome;
  outcomeSource: OutcomeSource;
  outcomeConfidence: number;
  /** Sentiment at the start and end of the session, -1..1. */
  sentiment: { start: number; end: number };
  friction: FrictionKind[];
  signals: SignalRef[];
  /** Business value attributed to the session by the agent's value model (null = none). */
  value: Money | null;
  enrichment: EnrichmentLevel;
  /** Two-sentence model-written summary (empty when metadata_only). */
  summary: string;
  /** Ids of the customer's funnels this session entered, with the last step reached. */
  funnels: Array<{ funnelId: string; reachedStep: number; converted: boolean }>;
}

export interface SessionTurn {
  index: number;
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  at: ISODate;
  traceId?: string;
  spanId?: string;
  name?: string;
  /** Conversation stage this turn belongs to (from the agent's discovered stage model). */
  stage?: string;
  sentiment?: number;
  friction?: FrictionKind[];
  signalIds?: string[];
  /** Funnel steps first reached on this turn. */
  funnelSteps?: Array<{ funnelId: string; stepId: string }>;
}

export interface OutcomeEvidence {
  source: OutcomeSource;
  label: string;
  detail: string;
  at: ISODate;
}

export interface SessionDetail extends SessionSummary {
  transcript: SessionTurn[];
  /** Ordered conversation stages the session went through. */
  path: string[];
  frictionMarkers: FrictionMarker[];
  outcomeEvidence: OutcomeEvidence[];
  /** Entities the model extracted (order ids, products, plans, repos...), redacted per privacy mode. */
  entities: Array<{ type: string; value: string }>;
  /** Things the user asked for that the agent could not do. */
  unmetRequests: string[];
  /** Questions the agent could not ground in a source. */
  knowledgeGaps: string[];
  /** Business events joined to the session (from track() or connectors). */
  events: Array<{ name: string; at: ISODate; source: "sdk" | "connector"; properties: Record<string, string | number | boolean> }>;
  enrichedAt: ISODate | null;
  enrichmentModel: string | null;
}

// ─── Use cases (discovered taxonomy) ─────────────────────────────────────────

export type UseCaseStatus = "discovered" | "confirmed" | "renamed" | "merged" | "hidden";

export interface UseCase {
  id: string;
  parentId: string | null;
  name: string;
  description: string;
  status: UseCaseStatus;
  agentIds: string[];
  sessions7d: number;
  /** Share of all sessions in scope, 0..1. */
  share: number;
  sessionsDeltaPct: number;
  successRate: number;
  successRateDelta: number;
  outcomes: Record<SessionOutcome, number>;
  avgTurns: number;
  avgDurationMs: number;
  costPerSession: number;
  costPerSuccess: number;
  avgSentimentEnd: number;
  frustrationRate: number;
  topFriction: Array<{ kind: FrictionKind; sessions: number }>;
  trend: number[];
  successTrend: number[];
  /** Third-party exposure of the actions taken in this use case over 30 days. */
  exposure30d: Money;
  examples: string[];
  discoveredAt: ISODate;
  children: number;
}

export interface UseCaseDetail extends UseCase {
  byVersion: Array<{ agentId: string; version: string; sessions: number; successRate: number }>;
  bySegment: Array<{ segment: string; sessions: number; successRate: number }>;
  paths: ConversationPaths;
  painPointIds: string[];
  sampleSessionIds: string[];
}

// ─── Conversation paths ──────────────────────────────────────────────────────

export interface PathNode {
  id: string;
  label: string;
  /** Column in the flow diagram (0 = first stage). */
  depth: number;
  terminal?: SessionOutcome;
}

export interface PathLink {
  source: string;
  target: string;
  sessions: number;
  successRate: number;
}

export interface PathSummary {
  stages: string[];
  sessions: number;
  successRate: number;
  avgTurns: number;
}

export interface ConversationPaths {
  agentId: string | null;
  useCaseId: string | null;
  nodes: PathNode[];
  links: PathLink[];
  /** Most common paths that end in success. */
  golden: PathSummary[];
  /** Most common paths that end in failure, escalation or abandonment. */
  failing: PathSummary[];
  /** Plain-language findings ("Asking for the order id twice converts 38% worse"). */
  findings: Array<{ text: string; liftPct: number; sessions: number }>;
}

// ─── Outcomes ────────────────────────────────────────────────────────────────

export interface OutcomeDefinition {
  id: string;
  name: string;
  /** What counts as success, failure or a business outcome. */
  kind: "success" | "failure" | "conversion";
  source: OutcomeSource;
  /** Event name, connector rule, or plain-language rubric for inferred outcomes. */
  rule: string;
  agentIds: string[] | null;
  useCaseId: string | null;
  /** Value attached per occurrence (e.g. "$6.20 human-ticket cost avoided", or "order amount"). */
  value: { kind: "fixed"; amount: Money } | { kind: "property"; property: string } | null;
  sessions7d: number;
  /** Session outcome asserted when it matches (null: the kind's default). */
  outcome?: SessionOutcome | null;
  /** `proposed` by discovery, `active` once accepted (or created), `dismissed` when declined. */
  status?: "proposed" | "active" | "dismissed";
  /** Why it was proposed, citing the evidence. */
  rationale?: string | null;
  /** Sessions of the use case that emitted the event when it was proposed. */
  support?: number | null;
  decidedBy?: string | null;
}

export interface OutcomesView {
  totals: Record<SessionOutcome, number>;
  successRate: number;
  successRateDelta: number;
  daily: Array<{ date: ISODate } & Record<SessionOutcome, number>>;
  byAgent: Array<{ agentId: string; agentName: string; sessions: number; successRate: number; delta: number }>;
  bySource: Record<OutcomeSource, number>;
  /**
   * Where the agent said a session was resolved but a later business signal contradicted it
   * (ticket reopened, refund not settled, user came back within 72 h with the same intent).
   */
  verificationGap: {
    claimedResolved: number;
    contradicted: number;
    rate: number;
    reasons: Array<{ reason: string; sessions: number }>;
    sampleSessionIds: string[];
  };
  definitions: OutcomeDefinition[];
}

// ─── Funnels ─────────────────────────────────────────────────────────────────

/**
 * A funnel step matches either something deterministic (an event the customer sent, a tool call, a
 * session outcome) or something semantic, written in plain language and evaluated by the analytics
 * model during enrichment ("user expressed purchase intent", "agent handled the price objection").
 */
export type FunnelStepKind = "event" | "tool_call" | "semantic" | "outcome" | "use_case";

export interface FunnelStep {
  id: string;
  name: string;
  kind: FunnelStepKind;
  /** Event name, tool name, outcome, use-case id, or the plain-language description. */
  match: string;
}

/**
 * A funnel suggested from the org's conversations: the use case, the tool calls and events its
 * successful sessions go through, and the outcome. Using it opens the builder pre-filled.
 */
export interface FunnelSuggestion {
  id: string;
  name: string;
  description: string;
  useCaseId: string | null;
  agentIds: string[];
  steps: Array<Omit<FunnelStep, "id">>;
  /** Sessions the suggestion was built from. */
  sessions: number;
  createdAt: ISODate;
}

export interface Funnel {
  id: string;
  name: string;
  description: string;
  owner: string;
  agentIds: string[];
  steps: FunnelStep[];
  /** Steps must happen within one session, or within a window across sessions of the same end user. */
  window: "session" | "24h" | "7d";
  status: "backfilling" | "ready";
  backfillProgress: number;
  createdAt: ISODate;
  entered7d: number;
  conversion7d: number;
  conversionDelta: number;
}

export interface FunnelStepResult {
  stepId: string;
  name: string;
  kind: FunnelStepKind;
  sessions: number;
  conversionFromPrev: number;
  conversionFromStart: number;
  medianSecondsFromPrev: number | null;
  /**
   * How the sessions that stopped after this step ended: failed (failed, escalated, abandoned or
   * only partly resolved), succeeded another way (resolved or converted without the remaining
   * steps), or not known yet.
   */
  dropped: { failed: number; succeeded: number; unknown: number };
  /**
   * Why sessions stopped after this step: friction seen in failed sessions, otherwise the outcome.
   * `exit` says which of the three groups above the reason belongs to; `share` is of all drops.
   */
  dropReasons: Array<{ reason: string; exit: "failed" | "succeeded" | "unknown"; share: number; sampleSessionIds: string[] }>;
}

export interface FunnelResult {
  funnel: Funnel;
  range: { from: ISODate; to: ISODate };
  steps: FunnelStepResult[];
  daily: Array<{ date: ISODate; entered: number; converted: number }>;
  byVersion: Array<{ agentId: string; version: string; entered: number; conversion: number }>;
  bySegment: Array<{ segment: string; entered: number; conversion: number }>;
}

// ─── Pain points ─────────────────────────────────────────────────────────────

export type PainPointKind = "friction" | "knowledge_gap" | "unmet_demand" | "failure_mode";
export type PainPointStatus = "new" | "acknowledged" | "tracking" | "resolved" | "dismissed";

export interface PainPoint {
  id: string;
  kind: PainPointKind;
  title: string;
  description: string;
  status: PainPointStatus;
  agentIds: string[];
  useCaseIds: string[];
  sessions7d: number;
  affectedUsers7d: number;
  deltaPct: number;
  trend: number[];
  /** Success rate of affected sessions minus that of comparable unaffected sessions. */
  successImpact: number;
  /** Cost of the pain point over 7 days (extra turns, escalations, lost value). */
  costImpact7d: Money;
  quotes: Array<{ text: string; sessionId: string }>;
  suggestion: string;
  /** A signal definition tracking this pain point, if the customer accepted one. */
  signalDefinitionId: string | null;
  issueId: string | null;
  firstSeen: ISODate;
}

// ─── Dynamic signals ─────────────────────────────────────────────────────────

/**
 * Two tiers. Analytics signals are anything a customer defines or accepts: they drive dashboards,
 * alerts and issues but never the reliability score or price. Risk signals are curated, versioned
 * and calibrated by traces.insure and are the only ones that feed ARS and pricing. A customer
 * signal can be nominated for certification as a risk signal.
 */
export type SignalTierClass = "analytics" | "risk";

/**
 * Cheapest first. `decision` asks a decision model (typed predicate / choice / score answers with
 * calibrated probabilities, no generated text) and thresholds the probability; `llm_rubric` asks a
 * generative model and is reserved for definitions that need reasoning or an explanation. The engine
 * distills a signal to a cheaper method once it is stable.
 */
export type SignalMethod = "rules" | "embedding" | "decision" | "llm_rubric";

export type SignalOrigin = "catalog" | "pack" | "discovered" | "custom";

export type SignalDefinitionStatus =
  /** Catalog signals specified but not detected yet (no signals, no issues). */
  | "planned"
  | "proposed"
  | "backtesting"
  | "shadow"
  | "active"
  | "paused"
  | "retired"
  | "dismissed";

export type CertificationStatus = "none" | "nominated" | "under_review" | "certified" | "rejected";

export interface SignalExample {
  text: string;
  sessionId: string | null;
  /** Positive = the signal should fire; negative = it should not. */
  positive: boolean;
}

export interface SignalDefinition {
  id: string;
  /** Machine key, e.g. "custom.unrequested_discount". */
  type: string;
  name: string;
  /** Plain-language definition; the rubric the analytics model applies. */
  description: string;
  family: SignalFamily;
  severity: Severity;
  tierClass: SignalTierClass;
  method: SignalMethod;
  /** Detection tier label used in the signal explorer (D/C/J/S). */
  detectionTier: DetectionTier;
  status: SignalDefinitionStatus;
  origin: SignalOrigin;
  pack: PackId | null;
  /** null = all agents. */
  agentIds: string[] | null;
  examples: SignalExample[];
  version: number;
  /** Measured from reviewed hits; null until enough labels. */
  precision: number | null;
  labeled: number;
  hits7d: number;
  hitTrend: number[];
  /** Estimated enrichment cost per 1,000 sessions, USD. */
  costPer1k: number;
  /** Open an issue when it fires (analytics signals can still create issues). */
  createsIssues: boolean;
  certification: CertificationStatus;
  createdBy: string;
  createdAt: ISODate;
  updatedAt: ISODate;
  /** Present on discovered proposals: why the engine suggests tracking this. */
  proposal?: SignalProposalEvidence;
  /** Latest backtest, if one ran. */
  backtest?: SignalBacktest;
  /** Fire when the probability / similarity score ≥ threshold (current version). */
  threshold: number;
  thresholdSource: "default" | "calibrated" | "manual";
  /** Version history, newest first. */
  versions: Array<{ version: number; method: SignalMethod; threshold: number; createdAt: ISODate; createdBy: string }>;
}

export interface SignalProposalEvidence {
  /** Sessions in the cluster the engine found. */
  clusterSessions: number;
  /** Not explained by any existing signal. */
  unexplainedShare: number;
  /** How these sessions differ in outcome ("31% end in a complaint vs 6% baseline"). */
  outcomeLift: string;
  reasoning: string;
  suggestedFix: string;
  sampleSessionIds: string[];
  discoveredAt: ISODate;
}

export interface SignalBacktest {
  definitionId: string;
  version: number;
  window: string;
  sessionsScanned: number;
  hits: number;
  hitRate: number;
  /** Precision estimated from a reviewed sample (or the model's self-agreement before review). */
  estPrecision: number;
  byAgent: Array<{ agentId: string; agentName: string; hits: number }>;
  daily: Array<{ date: ISODate; hits: number }>;
  samples: Array<{ sessionId: string; excerpt: string; confidence: number; outcome: SessionOutcome }>;
  outcomeOfHits: Record<SessionOutcome, number>;
  costUsd: number;
  ranAt: ISODate;
}

export interface ReviewItem {
  id: string;
  definitionId: string;
  signalName: string;
  sessionId: string;
  agentName: string;
  excerpt: string;
  explanation: string;
  confidence: number;
  label: "correct" | "incorrect" | "unsure" | null;
  labeledBy: string | null;
  createdAt: ISODate;
}

// ─── Insights & ask ──────────────────────────────────────────────────────────

export type InsightKind = "regression" | "improvement" | "trend" | "anomaly" | "opportunity" | "risk";

export interface Insight {
  id: string;
  kind: InsightKind;
  title: string;
  body: string;
  importance: Severity;
  createdAt: ISODate;
  metrics: Array<{ label: string; value: string; delta?: string; good?: boolean }>;
  links: Array<{ label: string; href: string }>;
  agentIds: string[];
  useCaseId: string | null;
  status: "new" | "read" | "dismissed";
}

export interface AskResult {
  question: string;
  answer: string;
  /** The read-only query the model generated, tenant-scoped and validated before running. */
  sql: string;
  table: { columns: string[]; rows: Array<Array<string | number | null>> } | null;
  chart: { kind: "bar" | "line"; x: string; y: string } | null;
  followUps: string[];
}

// ─── Economics ───────────────────────────────────────────────────────────────

export interface AgentEconomics {
  agentId: string;
  agentName: string;
  sessions30d: number;
  successes30d: number;
  /** Value delivered according to the agent's outcome definitions. */
  value: Money;
  cost: { model: Money; tools: Money; total: Money };
  /** Expected loss from the risk model over 30 days (frequency × severity at the current ARS). */
  expectedLoss: Money;
  premium: Money;
  net: Money;
  /** (value − cost − expected loss − premium) / (cost + premium). */
  roi: number;
  costPerSuccess: number;
  valueModel: string;
  byUseCase: Array<{ useCaseId: string; name: string; sessions: number; value: Money; cost: Money; expectedLoss: Money }>;
}

// ─── Analytics home ──────────────────────────────────────────────────────────

/**
 * POST /analytics/refresh (owners and admins): today's and yesterday's rollups are rebuilt before
 * the response; discovery (use cases, pain points, insights) runs in the background for a few minutes.
 */
/** Derived layers a rebuild can re-run (docs/17 §7). */
export const REBUILD_LAYERS = ["signals", "analytics_daily", "dashboard_rollups"] as const;
export type RebuildLayer = (typeof REBUILD_LAYERS)[number];

export interface RebuildStarted {
  id: string;
  layer: RebuildLayer;
  from: string;
  to: string;
  days: number;
}

export interface RebuildStatus {
  id: string;
  /** Workflow status: queued, running, paused, errored, terminated, complete, waiting… */
  status: string;
  output: unknown;
  error: string | null;
}

/** Agreement of a shadow decision model with the primary over recent sessions (docs/17 §6). */
export interface ShadowSummary {
  shadowModel: string;
  primaryModel: string;
  sessions: number;
  compared: number;
  agreed: number;
  agreement: number | null;
  /** Questions the models disagree on most, by count. */
  topDisagreements: Array<{ name: string; count: number }>;
  lastAt: string;
}

export interface AnalyticsRefresh {
  days: string[];
  discoveryStarted: boolean;
  /** Epoch ms after which the next refresh is accepted. */
  nextAllowedAt: number;
}

export interface AnalyticsOverview {
  sessions7d: number;
  sessionsDeltaPct: number;
  successRate: number;
  successRateDelta: number;
  frustrationRate: number;
  frustrationDelta: number;
  costPerSuccess: number;
  valueDelivered30d: Money;
  /** Share of sessions whose intent didn't match any use case (taxonomy needs a refresh). */
  unclassifiedShare: number;
  enrichedShare: number;
  outcomesDaily: OutcomesView["daily"];
  /**
   * Latest day whose analytics are final (its end plus the late-outcome window has passed, docs/17
   * §6). Later days are provisional: outcomes from business events can still arrive and change them.
   */
  finalThrough: string;
  topUseCases: UseCase[];
  risingPainPoints: PainPoint[];
  insights: Insight[];
  proposedSignals: number;
  funnels: Funnel[];
}

export interface VersionComparison {
  agentId: string;
  a: string;
  b: string;
  metrics: Array<{ name: string; a: number; b: number; unit: "pct" | "usd" | "count" | "ms" | "score"; better: "higher" | "lower"; significant: boolean }>;
  byUseCase: Array<{ useCaseId: string; name: string; a: number; b: number }>;
}

/**
 * Decision models answer typed questions with probabilities (no generated text). The provider is
 * swappable behind one client; Workers AI keeps content on Cloudflare, external providers are opt-in.
 */
export type DecisionProvider = "workers_ai_clef_flash" | "workers_ai_clef" | "jev" | "openai_decisions";

/** Aggregates over the same filters as the session list (GET /sessions/stats). */
export interface SessionStats {
  total: number;
  outcomes: Record<SessionOutcome, number>;
  successRate: number;
  frustrationRate: number;
  avgTurns: number;
  enrichedShare: number;
}

export interface EnrichmentSettings {
  /** Decision-model pass (outcome, use case, friction, funnel steps, decision signals) runs on every session. */
  decisionProvider: DecisionProvider;
  /** Generative pass (summary, intent text, entities, stages, unmet requests) on this share of sessions; flagged sessions always. */
  sampleRate: number;
  monthlyBudgetUsd: number;
  spentMtdUsd: number;
  /** Session closes after this much inactivity unless the SDK ends it explicitly. */
  idleTimeoutMinutes: number;
  contentRetentionDays: number;
  piiRedaction: true;
}

/**
 * Thrown by a data source when a requested state change isn't allowed from the resource's current
 * state (e.g. activating a signal definition that is still `proposed`). The API answers 409.
 */
export class InvalidTransitionError extends Error {
  readonly code = "invalid_transition";
}
